import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { verifySelfRegisterEmail } from "../api/selfRegister";
import { useLanguage } from "../i18n/LanguageContext";

const STORE = {
  ios: "https://apps.apple.com/us/app/talo/6767144859",
  android: "https://play.google.com/store/apps/details?id=com.kalyx.talo",
  windows:
    "https://github.com/jamesjaly007/talo-web/releases/download/v1.0.0/TaloSetup.exe",
};

export function VerifyEmailPage({ Layout }) {
  const { t } = useLanguage();
  const s = t.signup;
  const v = t.verifyEmail;
  const [searchParams] = useSearchParams();
  const token = String(searchParams.get("token") ?? "").trim();
  const [status, setStatus] = useState(token ? "loading" : "missing");
  const [error, setError] = useState("");
  const [adminLogin, setAdminLogin] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    (async () => {
      const res = await verifySelfRegisterEmail(token);
      if (cancelled) return;
      if (!res.ok) {
        setStatus("error");
        setError(res.error || v.genericError);
        return;
      }
      setAdminLogin(res.adminLogin || "");
      setEmailSent(Boolean(res.emailSent));
      setStatus("ok");
    })();
    return () => {
      cancelled = true;
    };
  }, [token, v.genericError]);

  return (
    <Layout hideFooter>
      <section className="verify-hero">
        <div className={`verify-panel verify-panel-${status}`}>
          <div className={`verify-accent verify-accent-${status}`} aria-hidden="true">
            <span className="verify-accent-label">{v.stepLabel}</span>
          </div>

          {status === "loading" ? (
            <div className="verify-body">
              <div className="verify-icon verify-icon-loading" aria-hidden="true">
                <i className="fa-solid fa-spinner fa-spin" />
              </div>
              <h1>{v.loadingTitle}</h1>
              <p>{v.loadingBody}</p>
            </div>
          ) : null}

          {status === "missing" ? (
            <div className="verify-body">
              <div className="verify-icon verify-icon-warn" aria-hidden="true">
                <i className="fa-solid fa-link-slash" />
              </div>
              <h1>{v.missingTitle}</h1>
              <p>{v.missingBody}</p>
              <Link className="verify-primary-btn" to="/inscription">
                {s.submit}
              </Link>
            </div>
          ) : null}

          {status === "error" ? (
            <div className="verify-body">
              <div className="verify-icon verify-icon-error" aria-hidden="true">
                <i className="fa-solid fa-circle-exclamation" />
              </div>
              <h1>{v.errorTitle}</h1>
              <p className="verify-error-text">{error}</p>
              <Link className="verify-primary-btn" to="/inscription">
                {v.retrySignup}
              </Link>
            </div>
          ) : null}

          {status === "ok" ? (
            <div className="verify-body">
              <div className="verify-icon verify-icon-ok" aria-hidden="true">
                <i className="fa-solid fa-circle-check" />
              </div>
              <h1>{v.successTitle}</h1>
              <p>{v.successBody}</p>
              {adminLogin ? (
                <div className="verify-login-chip">
                  <span>{s.successLoginPrefix}</span>
                  <strong>{adminLogin}</strong>
                </div>
              ) : null}
              {emailSent ? (
                <p className="verify-note">{s.emailSent}</p>
              ) : (
                <p className="verify-note verify-note-muted">{s.emailSkipped}</p>
              )}

              <p className="verify-download-label">{v.downloadLabel}</p>
              <div className="verify-store-row">
                <a href={STORE.ios} target="_blank" rel="noreferrer">
                  <img src={t.hero.appStoreBadge} alt={t.hero.appStoreAlt} />
                </a>
                <a href={STORE.android} target="_blank" rel="noreferrer">
                  <img src={t.hero.playStoreBadge} alt={t.hero.playStoreAlt} />
                </a>
                <a
                  className="windows-download-btn"
                  href={STORE.windows}
                  target="_blank"
                  rel="noreferrer"
                >
                  <div className="windows-icon">
                    <i className="fa-brands fa-windows" />
                  </div>
                  <div className="windows-text">
                    <span>{t.footer.downloadOn}</span>
                    <strong> {t.footer.windows}</strong>
                  </div>
                </a>
              </div>

              <Link className="verify-home-link" to="/">
                {s.backHome}
              </Link>
            </div>
          ) : null}
        </div>
      </section>
    </Layout>
  );
}
